import { Currency, Ether, NativeCurrency, Token, WETH9 } from '@x-swap-protocol/sdk-core'
import { SupportedChainId } from 'constants/chains'
import invariant from 'tiny-invariant'

import { UNI_ADDRESS } from './addresses'

export const NATIVE_CHAIN_ID = 'XDC'

export const DEFAULT_ERC20_DECIMALS = 18

export const XSP_XDC = new Token(
  SupportedChainId.XDC,
  '0x36726235dAdbdb4658D33E62a249dCA7c4B2bC68',
  18,
  'XSP',
  'XSwapProtocol'
)

export const XTT_XDC = new Token(
  SupportedChainId.XDC,
  '0x17476dc3eda45aD916cEAdDeA325B240A7FB259D',
  18,
  'XTT',
  'XSwap Treasury Token'
)

export const NATIVE_USDC_XDC = new Token(
  SupportedChainId.XDC,
  '0xfa2958cb79b0491cc627c1557f441ef849ca8eb1',
  6,
  'USDC',
  'USDC'
)

export const USDC_XDC = new Token(
  SupportedChainId.XDC,
  '0x2a8e898b6242355c290e1f4fc966b8788729a4d4',
  6,
  'USDC.e',
  'Bridged USDC(XDC)'
)

export const USDC_APOTHEM = new Token(
  SupportedChainId.XDC_TESTNET,
  '0x7a619b7667ef680266f3c5eba5c98b9c63cec0ba',
  18,
  'MP7',
  'MaiPhuong7'
)

export const UNI: { [chainId: number]: Token } = {}

export const XDC: { [chainId: number]: Token } = {
  [SupportedChainId.XDC]: new Token(SupportedChainId.XDC, UNI_ADDRESS[50], 18, 'XDC', 'Xswap'),
  [SupportedChainId.XDC_TESTNET]: new Token(SupportedChainId.XDC_TESTNET, UNI_ADDRESS[51], 18, 'XDC', 'Xswap'),
}

export const WRAPPED_NATIVE_CURRENCY: { [chainId: number]: Token | undefined } = {
  ...(WETH9 as Record<SupportedChainId, Token>),
}

function isXdc(chainId: number): chainId is SupportedChainId.XDC | SupportedChainId.XDC_TESTNET {
  return chainId === SupportedChainId.XDC || chainId === SupportedChainId.XDC_TESTNET
}

class XdcNativeCurrency extends NativeCurrency {
  equals(other: Currency): boolean {
    return other.isNative && other.chainId === this.chainId
  }

  get wrapped(): Token {
    if (!isXdc(this.chainId)) throw new Error('Not xdc')
    const wrapped = WRAPPED_NATIVE_CURRENCY[this.chainId]
    invariant(wrapped instanceof Token)
    return wrapped
  }

  public constructor(chainId: number) {
    if (!isXdc(chainId)) throw new Error('Not xdc')
    super(chainId, 18, 'XDC', 'XDC')
  }
}

class ExtendedEther extends Ether {
  public get wrapped(): Token {
    const wrapped = WRAPPED_NATIVE_CURRENCY[this.chainId]
    if (wrapped) return wrapped
    throw new Error(`Unsupported chain ID: ${this.chainId}`)
  }

  private static _cachedExtendedEther: { [chainId: number]: NativeCurrency } = {}

  public static onChain(chainId: number): ExtendedEther {
    return this._cachedExtendedEther[chainId] ?? (this._cachedExtendedEther[chainId] = new ExtendedEther(chainId))
  }
}

const cachedNativeCurrency: { [chainId: number]: NativeCurrency | Token } = {}
export function nativeOnChain(chainId: number): NativeCurrency | Token {
  if (cachedNativeCurrency[chainId]) return cachedNativeCurrency[chainId]
  let nativeCurrency: NativeCurrency | Token
  if (isXdc(chainId)) {
    nativeCurrency = new XdcNativeCurrency(chainId)
  } else {
    nativeCurrency = ExtendedEther.onChain(chainId)
  }
  return (cachedNativeCurrency[chainId] = nativeCurrency)
}

export function getSwapCurrencyId(currency: Currency): string {
  if (currency.isToken) {
    return currency.address
  }
  return NATIVE_CHAIN_ID
}

export const TOKEN_SHORTHANDS: { [shorthand: string]: { [chainId in SupportedChainId]?: string } } = {
  USDC: {
    [SupportedChainId.XDC]: NATIVE_USDC_XDC.address,
    [SupportedChainId.XDC_TESTNET]: USDC_APOTHEM.address,
  },
}
